import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types';
import { apiService } from '../services/api';
import { menuItems } from '../data/menuData';

const API_BASE_URL = import.meta.env.VITE_API_URL;

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string, twoFactorCode?: string) => Promise<boolean | { requiresTwoFactor: boolean }>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  isAuthenticated: boolean;
  hasMenuPermission: (menuName: string, action?: string) => boolean;
  hasPermission: (permission: string) => boolean;
  hasRole: (roles: string | string[]) => boolean;
  isLoading: boolean;
  userMenus: any[];
  getRolePermissionTemplate: (role: string) => { menus: string[], actions: string[] };
  loadUserMenus: (userId: number) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userMenus, setUserMenus] = useState<any[]>([]);
  const [userRolePermissions, setUserRolePermissions] = useState<any>({});

  const loadUserRolePermissions = async (userId: number) => {
    try {
      const response = await apiService.getUserRolePermissions(userId);
      const permissionsMap: any = {};
      
      if (response.permissions) {
        response.permissions.forEach((perm: any) => {
          const key = `${perm.menu_id}_${perm.permission_type}`;
          permissionsMap[key] = perm.granted;
        });
      }
      
      setUserRolePermissions(permissionsMap);
    } catch (error) {
      console.error('Error loading user role permissions:', error);
      setUserRolePermissions({});
    }
  };

  const loadUserMenus = async (userId: number, forceRefresh: boolean = false) => {
    try {
      // Load user menus from backend
      const response = await fetch(`${API_BASE_URL}/menu-management/user-menu`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (response.ok) {
        const menus = await response.json();
        setUserMenus(menus || []);
      } else {
        setUserMenus([]);
      }

      // Also load role permissions for fine-grained control
      await loadUserRolePermissions(userId);

    } catch (error) {
      console.error('Error loading user menus:', error);
      setUserMenus([]);
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const savedUser = localStorage.getItem('auth_user');
        const savedToken = localStorage.getItem('auth_token');
        
        if (savedUser && savedToken) {
          const userData = JSON.parse(savedUser);
          console.log('[AuthContext] Loaded user from localStorage:', userData);

          // Verify token with server
          try {
            const response = await apiService.getCurrentUser();
            console.log('[AuthContext] Got user from API:', response);
            setUser(userData);
            // Load user menus
            await loadUserMenus(userData.id);
            // Start token refresh timer
            startTokenRefresh();
          } catch (verifyError) {
            clearAuthData();
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        clearAuthData();
      } finally {
        setIsLoading(false);
      }
    };
    
    const clearAuthData = () => {
      localStorage.removeItem('auth_user');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_token_expiry');
      setUser(null);
      setUserMenus([]);
      setUserRolePermissions({});
    };
    
    // Token refresh function
    const refreshToken = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        if (!token) return;
        
        const response = await fetch(`${API_BASE_URL}/refresh/token`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          localStorage.setItem('auth_token', data.token);
        } else {
          logout();
        }
      } catch (error) {
        console.error('Token refresh error:', error);
        logout();
      }
    };
    
    // Start token refresh timer (every 7 hours 50 minutes - 10 minutes before expiry)
    const startTokenRefresh = () => {
      const interval = setInterval(refreshToken, 7 * 60 * 60 * 1000 + 50 * 60 * 1000); // 7h 50m
      return () => clearInterval(interval);
    };
    
    initializeAuth();
    
    // Listen for permission updates
    const handlePermissionUpdate = (event: CustomEvent) => {
      const { userId } = event.detail;
      if (user && user.id === userId) {
        loadUserMenus(userId, true);
      }
    };
    
    window.addEventListener('userPermissionsUpdated', handlePermissionUpdate as EventListener);
    
    return () => {
      window.removeEventListener('userPermissionsUpdated', handlePermissionUpdate as EventListener);
    };
  }, []);

  const login = async (email: string, password: string, twoFactorCode?: string): Promise<boolean | { requiresTwoFactor: boolean }> => {
    if (isLoading) {
      return false;
    }
    
    setIsLoading(true);
    
    try {
      // Direct API call to backend
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password, twoFactorCode })
      });
      
      const data = await response.json();
      
      // 2FA gerekli mi kontrol et (response.ok olmasına bakmadan)
      if (data.requiresTwoFactor === true) {
        setIsLoading(false);
        return { requiresTwoFactor: true };
      }
      
      if (!response.ok) {
        setIsLoading(false);
        if (response.status === 429) {
          throw new Error('Too many login attempts, please try again later.');
        }
        throw new Error(data.error || `HTTP ${response.status}`);
      }
      
      if (data.success && data.token && data.user) {
        console.log('[AuthContext] Login successful. User data:', data.user);
        // Save to localStorage
        localStorage.setItem('auth_user', JSON.stringify(data.user));
        localStorage.setItem('auth_token', data.token);

        setUser(data.user);
        
        // Load user menus (force refresh on login)
        await loadUserMenus(data.user.id, true);
        
        // Trigger login notification event
        window.dispatchEvent(new CustomEvent('userLogin', { detail: data.user }));
        

        
        setIsLoading(false);
        
        
        return true;
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      setIsLoading(false);
      console.error('Login error:', error);
      throw error;
    }
  };

  const refreshUser = async (): Promise<void> => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;
      
      const response = await apiService.getCurrentUser();
      setUser(response);
      localStorage.setItem('auth_user', JSON.stringify(response));
      
      // Refresh user menus when user data is refreshed
      if (response && response.id) {
        await loadUserMenus(response.id, true);
      }
    } catch (error) {
      console.error('Error refreshing user:', error);
    }
  };

  const logout = () => {
    // Call logout API
    fetch(`${API_BASE_URL}/auth/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
      }
    }).then(response => {
    }).catch(error => {
      console.error('Logout API error:', error);
    });
    
    // Clear localStorage
    localStorage.removeItem('auth_user');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_token_expiry');
    
    // Clear user menu cache
    if (user && user.id) {
      localStorage.removeItem(`user_menus_${user.id}`);
    }
    
    setUser(null);
    setUserRolePermissions({});
  };

  const hasMenuPermission = (menuName: string, action: string = 'view'): boolean => {
    if (!user) {
      return false;
    }

    // Dashboard is always accessible for all authenticated users
    if (menuName === 'dashboard') {
      return true;
    }

    // Superadmin has access to all menus
    if (user.role === 'superadmin') {
      return true;
    }

    // Use role-based permissions from menuData
    const result = checkRoleBasedPermission(menuName, user.role);
    return result;
  };

  const checkRoleBasedPermission = (menuName: string, role: string): boolean => {
    // Dashboard is always accessible for all authenticated users
    if (menuName === 'dashboard') return true;

    // Superadmin has access to all menus
    if (role === 'superadmin') return true;

    // Normalize menuName for comparison (convert hyphens to underscores and vice versa)
    const normalize = (str: any) => String(str || '').toLowerCase().replace(/-/g, '_').replace(/_/g, '-');
    const normalizedMenuName = normalize(menuName);

    // Find menu from userMenus (dynamically loaded from backend)
    // userMenus is populated by loadUserMenus from /api/menu-management/user-menu
    let foundMenu: any = null;

    const searchMenus = (menus: any[]): void => {
      for (const menu of menus) {
        // Try to match with path/route field
        if (menu.route) {
          const pathMenuName = normalize(menu.route.replace(/\//g, ''));
          if (pathMenuName === normalizedMenuName) {
            foundMenu = menu;
            return;
          }
        }

        // Try to match with id field (for database lookups)
        if (menu.id && normalize(menu.id) === normalizedMenuName) {
          foundMenu = menu;
          return;
        }

        // Try to match with name field
        if (menu.name && normalize(menu.name) === normalizedMenuName) {
          foundMenu = menu;
          return;
        }

        // Try to match with label field
        if (menu.label && normalize(menu.label) === normalizedMenuName) {
          foundMenu = menu;
          return;
        }

        // Recursive search for child menus
        if (menu.children && Array.isArray(menu.children)) {
          searchMenus(menu.children);
          if (foundMenu) return;
        }
      }
    };

    // Search in user menus (from backend)
    if (Array.isArray(userMenus) && userMenus.length > 0) {
      searchMenus(userMenus);
    }

    // If menu found, check if user's role is in the menu's roles array
    if (foundMenu && foundMenu.roles) {
      const allowedRoles = Array.isArray(foundMenu.roles) ? foundMenu.roles : [foundMenu.roles];
      return allowedRoles.includes(role);
    }

    // Check backend role permissions as fallback (from role_permissions table)
    // userRolePermissions format: "menu_id_permission_type" -> granted (1/0)
    if (foundMenu && foundMenu.id) {
      const permissionKey = `${foundMenu.id}_view`;
      if (userRolePermissions.hasOwnProperty(permissionKey)) {
        const result = userRolePermissions[permissionKey] === 1;
        if (result) return true;
      }
    }

    return false;
  };

  const hasRole = (roles: string | string[]): boolean => {
    if (!user) return false;
    
    const roleArray = Array.isArray(roles) ? roles : [roles];
    return roleArray.includes(user.role);
  };

  const hasPermission = (permission: string): boolean => {
    if (!user) return false;

    // Superadmin has all permissions
    if (user.role === 'superadmin') return true;

    // Use role-based permissions from menuData
    const result = checkRoleBasedActionPermission(permission, user.role);
    return result;
  };

  const checkRoleBasedActionPermission = (permission: string, role: string): boolean => {
    // Dashboard is always accessible for all authenticated users
    if (permission === 'dashboard.view') return true;

    // Superadmin has all permissions
    if (role === 'superadmin') return true;

    // Check userRolePermissions first (from role_permissions table)
    // Permission format: "services.read" -> find menu_id and check with permission_type
    // userRolePermissions format: "menu_id_permission_type" -> granted (1/0)
    
    // Search through all userRolePermissions to find matching permission
    for (const [key, granted] of Object.entries(userRolePermissions)) {
      if (key.endsWith(`_${permission}`) && granted === 1) {
        return true;
      }
    }
    
    // Also check for exact permission match (fallback)
    if (userRolePermissions[permission] === 1) {
      return true;
    }
    
    const [menuName, action] = permission.split('.');
    
    // Check permissions based on menuData
    for (const menu of menuItems) {
      if (menu.id === menuName || menu.href === `/${menuName}`) {
        return menu.role_access?.includes(role) || false;
      }
      
      if (menu.children) {
        for (const child of menu.children) {
          if (child.id === menuName || child.href === `/${menuName}`) {
            return child.role_access?.includes(role) || false;
          }
        }
      }
    }
    
    return false;
  };

  // Role-based quick permission templates
  const getRolePermissionTemplate = (role: string) => {
    const templates = {
      'superadmin': {
        menus: ['*'],
        actions: ['view', 'create', 'update', 'delete']
      },
      'admin': {
        menus: [
          'dashboard',
          'arac_gerec',
          'companies',
          'locations',
          'suppliers',
          'equipment',
          'consumables',
          'documents',
          'maintenance_contracts',
          'damage_theft',
          'orders',
          'capex',
          'printers',
          'technical_service',
          'gsm_lines',
          'servers',
          'industrial',
          'network',
          'storage',
          'security_cameras',
          'meetings',
          'assignment_forms',
          'assignment_logs',
          'settings',
          'assignment_definitions',
          'users',
          'profile'
        ],
        actions: ['view', 'create', 'update']
      },
      'teknisyen': {
        menus: [
          'dashboard',
          'arac_gerec',
          'suppliers',
          'technical_service',
          'industrial',
          'network',
          'security_cameras',
          'profile'
        ],
        actions: ['view', 'update']
      },
      'calisan': {
        menus: ['dashboard', 'technical_service', 'profile'],
        actions: ['view']
      }
    };

    return templates[role] || { menus: ['dashboard'], actions: ['view'] };
  };

  const value: AuthContextType = {
    user,
    login,
    logout,
    refreshUser,
    isAuthenticated: !!user,
    hasMenuPermission,
    hasPermission,
    hasRole,
    isLoading,
    userMenus,
    getRolePermissionTemplate,
    loadUserMenus: (userId: number) => loadUserMenus(userId, true)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
